import { useState, useRef, useCallback } from 'react';
import { BETTY_API_URL } from '../config/api';

interface UseVoiceInputReturn {
  isRecording: boolean;
  isTranscribing: boolean;
  audioLevel: number;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<string | null>;
  error: string | null;
}

// Audio preprocessing function to trim silence and reduce file size
async function preprocessAudio(audioBlob: Blob): Promise<Blob | null> {
  try {
    // Create audio context for processing
    const audioContext = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    const arrayBuffer = await audioBlob.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

    // Get audio data
    const channelData = audioBuffer.getChannelData(0);
    const sampleRate = audioBuffer.sampleRate;
    const duration = audioBuffer.duration;

    // Find speech segments by analyzing audio levels
    const speechSegments = findSpeechSegments(channelData, sampleRate);

    if (speechSegments.length === 0) {
      audioContext.close();
      return null;
    }

    // Trim to speech segments with small padding
    const padding = 0.1; // 100ms padding
    const startTime = Math.max(0, speechSegments[0].start - padding);
    const endTime = Math.min(duration, speechSegments[speechSegments.length - 1].end + padding);

    // Extract speech portion
    const startSample = Math.floor(startTime * sampleRate);
    const endSample = Math.floor(endTime * sampleRate);
    const trimmedData = channelData.slice(startSample, endSample);

    // Create new audio buffer with trimmed data
    const trimmedBuffer = audioContext.createBuffer(1, trimmedData.length, sampleRate);
    trimmedBuffer.copyToChannel(trimmedData, 0);

    // Convert back to blob
    const processedBlob = await audioBufferToBlob(trimmedBuffer);

    audioContext.close();
    return processedBlob;

  } catch {
    return audioBlob; // Return original if processing fails
  }
}

// Find speech segments by analyzing audio levels
function findSpeechSegments(channelData: Float32Array, sampleRate: number) {
  const windowSize = Math.floor(0.1 * sampleRate); // 100ms windows
  const threshold = 0.01; // Silence threshold
  const minSegmentLength = 0.2; // Minimum 200ms segment

  const segments: { start: number; end: number }[] = [];
  let currentSegment: { start: number; end: number } | null = null;

  for (let i = 0; i < channelData.length; i += windowSize) {
    const window = channelData.slice(i, i + windowSize);
    const rms = Math.sqrt(window.reduce((sum, sample) => sum + sample * sample, 0) / window.length);

    if (rms > threshold) {
      // Speech detected
      const time = i / sampleRate;
      if (!currentSegment) {
        currentSegment = { start: time, end: time };
      } else {
        currentSegment.end = time;
      }
    } else {
      // Silence detected
      if (currentSegment) {
        const segmentDuration = currentSegment.end - currentSegment.start;
        if (segmentDuration >= minSegmentLength) {
          segments.push(currentSegment);
        }
        currentSegment = null;
      }
    }
  }

  // Add final segment if exists
  if (currentSegment) {
    const segmentDuration = currentSegment.end - currentSegment.start;
    if (segmentDuration >= minSegmentLength) {
      segments.push(currentSegment);
    }
  }

  return segments;
}

// Convert audio buffer to blob
async function audioBufferToBlob(audioBuffer: AudioBuffer): Promise<Blob> {
  const numberOfChannels = audioBuffer.numberOfChannels;
  const length = audioBuffer.length;
  const sampleRate = audioBuffer.sampleRate;

  // Create WAV file header
  const buffer = new ArrayBuffer(44 + length * numberOfChannels * 2);
  const view = new DataView(buffer);

  // WAV header
  const writeString = (offset: number, string: string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };

  writeString(0, 'RIFF');
  view.setUint32(4, 36 + length * numberOfChannels * 2, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numberOfChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numberOfChannels * 2, true);
  view.setUint16(32, numberOfChannels * 2, true);
  view.setUint16(34, 16, true);
  writeString(36, 'data');
  view.setUint32(40, length * numberOfChannels * 2, true);

  // Convert audio data to 16-bit PCM
  let offset = 44;
  for (let i = 0; i < length; i++) {
    for (let channel = 0; channel < numberOfChannels; channel++) {
      const sample = Math.max(-1, Math.min(1, audioBuffer.getChannelData(channel)[i]));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
      offset += 2;
    }
  }

  return new Blob([buffer], { type: 'audio/wav' });
}

export const useVoiceInput = (): UseVoiceInputReturn => {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const isRecordingRef = useRef(false);
  const autoStopTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const updateAudioLevel = useCallback(() => {
    if (analyserRef.current && isRecording) {
      const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
      analyserRef.current.getByteFrequencyData(dataArray);

      // Calculate average audio level
      const average = dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length;
      setAudioLevel(average / 255); // Normalize to 0-1

      animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
    }
  }, [isRecording]);

  const startRecording = useCallback(async () => {
    try {
      setError(null);

      // Check for MediaRecorder support
      if (!navigator.mediaDevices || !window.MediaRecorder) {
        throw new Error('Voice recording is not supported in this browser');
      }

      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        }
      });

      streamRef.current = stream;

      // Set up audio analysis for level monitoring
      const audioContext = new AudioContext();
      const analyser = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      // Set up MediaRecorder with fallback for better compatibility
      let mediaRecorder;
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        mediaRecorder = new MediaRecorder(stream, {
          mimeType: 'audio/webm;codecs=opus'
        });
      } else if (MediaRecorder.isTypeSupported('audio/webm')) {
        mediaRecorder = new MediaRecorder(stream, {
          mimeType: 'audio/webm'
        });
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mediaRecorder = new MediaRecorder(stream, {
          mimeType: 'audio/mp4'
        });
      } else {
        mediaRecorder = new MediaRecorder(stream);
      }

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.start(100); // Collect data every 100ms
      setIsRecording(true);
      isRecordingRef.current = true;

      // Auto-stop recording after 30 seconds to prevent excessive costs
      // Clear any existing timeout first
      if (autoStopTimeoutRef.current) {
        clearTimeout(autoStopTimeoutRef.current);
      }
      autoStopTimeoutRef.current = setTimeout(() => {
        if (isRecordingRef.current) {
          // Fire and forget - the UI will update when stopRecording completes
          void stopRecording();
        }
      }, 30000);

      // Start audio level monitoring
      updateAudioLevel();

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start recording');

      // Clean up on error
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    }
  }, [updateAudioLevel]);

  const stopRecording = useCallback(async (): Promise<string | null> => {
    return new Promise((resolve) => {
      if (!mediaRecorderRef.current || !isRecordingRef.current) {
        resolve(null);
        return;
      }

      // Clear auto-stop timeout
      if (autoStopTimeoutRef.current) {
        clearTimeout(autoStopTimeoutRef.current);
        autoStopTimeoutRef.current = null;
      }

      setIsRecording(false);
      isRecordingRef.current = false;
      setAudioLevel(0);

      // Stop audio level monitoring
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      // Clean up audio context
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      analyserRef.current = null;

      // Stop MediaRecorder
      mediaRecorderRef.current.onstop = async () => {
        try {
          setIsTranscribing(true);

          // Create audio blob with the same MIME type as the MediaRecorder
          const mimeType = mediaRecorderRef.current?.mimeType || 'audio/webm';
          const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });

          // Check if we have any audio data
          if (audioBlob.size === 0) {
            setError('No audio recorded');
            setIsTranscribing(false);
            resolve(null);
            return;
          }

          // Preprocess audio to trim silence and reduce size
          const processedBlob = await preprocessAudio(audioBlob);
          if (!processedBlob) {
            setError('No speech detected in recording');
            setIsTranscribing(false);
            resolve(null);
            return;
          }

          // Send to transcription API with appropriate file extension
          const formData = new FormData();
          const fileExtension = mimeType.includes('mp4') ? 'mp4' : 'webm';
          formData.append('audio', processedBlob, `recording.${fileExtension}`);

          const response = await fetch(`${BETTY_API_URL}/transcribe`, {
            method: 'POST',
            body: formData
          });

          if (!response.ok) {
            const errorText = await response.text();
            let errorMessage = `Transcription failed: ${response.statusText}`;

            try {
              const errorData = JSON.parse(errorText);
              if (errorData.error) {
                errorMessage = errorData.error;
              }
            } catch {
              // If we can't parse the error, use the status text
            }

            throw new Error(errorMessage);
          }

          const result = await response.json();

          if (result.success && result.text) {
            setError(null);
            resolve(result.text);
          } else {
            setError('No speech detected');
            resolve(null);
          }

        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to transcribe audio');
          resolve(null);
        } finally {
          setIsTranscribing(false);

          // Clean up stream
          if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
          }

          // Reset refs
          mediaRecorderRef.current = null;
          audioChunksRef.current = [];
        }
      };

      mediaRecorderRef.current.stop();
    });
  }, []);

  return {
    isRecording,
    isTranscribing,
    audioLevel,
    startRecording,
    stopRecording,
    error
  };
};
